import React from 'react';
import { gql, useMutation } from '@apollo/client';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';

import { GenericResponse } from 'types/schema.type';
import { AFibChart } from './AFibChart';
import { getTime } from '../utils/getTime';
import { formatSeconds } from '../utils/formatSeconds';
import { SequenceEpisodes } from '../utils/getSequenceEpisodes';

export const EDIT_INTERVAL_ANNOTATION = gql`
  mutation EditInterval($input: EditIntervalInput!) {
    editInterval(input: $input) {
      error
      success
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    width: 550,
  },
  cell: {
    border: 'none',
  },
  chart: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  cellSummary: {
    borderBottom: 'none',
    borderTop: '1px solid rgba(224, 224, 224, 1)',
  },
}));

type AFibEpisodesWithChartsProps = {
  afibs: SequenceEpisodes['afibs'];
  rpeaks: number[];
  edit?: boolean;
};

export function AFibTableWithCharts(props: AFibEpisodesWithChartsProps) {
  const classes = useStyles();
  const { afibs, rpeaks, edit } = props;

  const [editInterval] = useMutation<{
    editInterval: GenericResponse;
  }>(EDIT_INTERVAL_ANNOTATION);

  const getDuration = (from: number, to: number) => (to - from) / 200;

  const getTotalDuration = () => {
    const time = afibs?.reduce((acc, ep) => {
      if (ep.isValid) {
        return acc + getDuration(ep.startIndex, ep.endIndex);
      }

      return acc;
    }, 0);

    return formatSeconds(time);
  };

  const handleEditInterval = async (episode) => {
    if (!episode) return;

    await editInterval({
      variables: {
        input: {
          annotationId: episode.id,
          startIndex: episode.startIndex,
          endIndex: episode.endIndex,
          isValid: !episode.isValid,
        },
      },
      update(cache) {
        cache.modify({
          id: cache.identify(episode),
          fields: {
            isValid(existingIsValid = false) {
              return !existingIsValid;
            },
          },
        });
      },
    });
  };

  const episodesToShow = edit ? afibs : afibs?.filter((ep) => ep.isValid);

  return (
    <Container className={classes.root} component={Paper}>
      <Typography className={classes.title} variant="h6">
        Atrial Fibrillation Episodes with Charts
      </Typography>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Duration</TableCell>
            {edit && <TableCell>Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {episodesToShow?.map((ep) => {
            const duration = getDuration(ep.startIndex, ep.endIndex);
            const formattedDuration = formatSeconds(duration);

            return (
              <React.Fragment key={ep.id}>
                <TableRow>
                  <TableCell className={classes.cell}>
                    {getTime(ep.recordingStartedAt ?? 0, ep.startIndex)}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {getTime(ep.recordingStartedAt ?? 0, ep.endIndex)}
                  </TableCell>
                  <TableCell className={classes.cell}>{formattedDuration}</TableCell>
                  {edit && (
                    <TableCell className={classes.cell}>
                      <IconButton edge="end" aria-label="" onClick={() => handleEditInterval(ep)}>
                        {ep.isValid ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
                {ep.isValid && ep?.excerpt?.ch1.length > 0 && (
                  <TableRow>
                    <TableCell className={classes.cell} colSpan={edit ? 4 : 3}>
                      <div className={classes.chart}>
                        <AFibChart
                          recordingStartedAt={ep.recordingStartedAt ?? 0}
                          startIndex={ep.startIndex}
                          endIndex={ep.endIndex}
                          signal={ep.excerpt.ch1}
                          rpeaks={rpeaks}
                          showTitle={false}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
          <TableRow>
            <TableCell colSpan={1} />
            <TableCell className={classes.cellSummary}>Total</TableCell>
            <TableCell className={classes.cellSummary}>{getTotalDuration()}</TableCell>
            {edit && <TableCell className={classes.cellSummary} />}
          </TableRow>
        </TableBody>
      </Table>
    </Container>
  );
}
